import { computed, onBeforeMount, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import useUserCountry from '@/micro/composables/user/useUserCountry'
import useUserOrigin from '@/micro/composables/user/useUserOrigin'
import { useRegisterStore } from './register'
import { DEFAULT_COUNTRY } from '@/micro/modules/version'
import { ApplicationVersion } from '@/micro/types/version.type'
import { OriginKeyword } from '@/micro/types/register.type'
import { Product } from '@/micro/types/product.type'
import { getTranslations } from '@/micro/languages/es'
import i18n from '@/micro/languages'
import { getProductInfo } from '@/micro/utils/product'
import { getQueryParameterByName } from '../utils/urls'

export const useRootStore = defineStore('root', () => {
  const { getApplicationVersion } = useUserCountry()
  const { getOriginKeyword, getOnboardingType } = useUserOrigin()
  const registerStore = useRegisterStore()

  const loadingRef = ref<boolean>(false)
  const countryRef = ref<ApplicationVersion>(DEFAULT_COUNTRY)
  const productRef = ref<Product>('AC')
  const productLogoRef = ref<string>('alegra')
  const productNameRef = ref<string>('Alegra Contabilidad')
  const productUrlRef = ref<string>('https://www.alegra.com')

  const loading = computed({
    get: () => loadingRef.value,
    set: (value: boolean) => (loadingRef.value = value),
  })

  const country = computed({
    get: () => countryRef.value,
    set: (value: ApplicationVersion) => (countryRef.value = value),
  })

  const product = computed(() => productRef.value)
  const productLogo = computed(() => productLogoRef.value)
  const productName = computed(() => productNameRef.value)
  const productUrl = computed(() => productUrlRef.value)
  const isPOS = computed(() => ['POS', 'TPV', 'PUNTO-VENTA'].includes(product.value))
  const isPayroll = computed(() => product.value === 'AN')
  const isAC = computed(() => product.value === 'AC')
  const isSF = computed(() => getQueryParameterByName('alegra_source') === 'planes-solo-facturacion')

  const updateProductInfo = (
    currentCountry: ApplicationVersion,
    currentOriginKeyword: OriginKeyword
  ) => {
    const productInfo = getProductInfo(currentCountry, currentOriginKeyword)
    productRef.value = productInfo.product
    productNameRef.value = productInfo.name
    productLogoRef.value = productInfo.logo
    productUrlRef.value = productInfo.url
  }

  onBeforeMount(async () => {
    // Obtener la versión de la aplicación si no está en la ruta de registro del experimento
    // ya que ahi se pone la versión de la aplicación manualmente
    const routesExp = ['/user/register/exp1', '/user/register/exp2', '/user/register/exp3']
    if (!routesExp.includes(window.location.pathname)) {
      const applicationVersion = await getApplicationVersion()
      country.value = applicationVersion as ApplicationVersion
    }
  })

  // Actualiza la información del producto cuando cambia el origen
  watch(
    () => registerStore.originKeyword,
    originKeyword => updateProductInfo(country.value, originKeyword)
  )

  // Actualiza el información del producto cuando cambia el país
  watch(
    () => country.value,
    newCountry => updateProductInfo(newCountry, registerStore.originKeyword)
  )

  // Actualiza las traducciones/origen/tipo de onboarding cuando cambia el país
  watch(
    () => country.value,
    newCountry => {
      i18n.global.setLocaleMessage('es', getTranslations(newCountry))
      registerStore.originKeyword = getOriginKeyword()
      registerStore.onboardingType = getOnboardingType()
    },
    { immediate: true }
  )

  return {
    loading,
    country,
    product,
    productLogo,
    productName,
    isPOS,
    isPayroll,
    productUrl,
    isSF,
    isAC,
  }
})
